import React, { useRef } from 'react';
import { Paperclip, X, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import type { DailyLogAttachment } from '../types';

interface ExistingAttachmentsProps {
  attachments: DailyLogAttachment[];
  pendingFiles?: File[];
  isUploading?: boolean;
  onAttach: (files: File[]) => void;
  onRemoveExisting?: (uuid: string) => void;
  onRemovePending?: (index: number) => void;
}

export const AttachmentsCell: React.FC<ExistingAttachmentsProps> = ({
  attachments,
  pendingFiles = [],
  isUploading = false,
  onAttach,
  onRemoveExisting,
  onRemovePending,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleClick = () => fileInputRef.current?.click();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    if (files.length > 0) {
      onAttach(files);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const hasItems = attachments.length > 0 || pendingFiles.length > 0;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {attachments.map((attachment) => (
        <a
          key={attachment.uuid}
          href={attachment.file_url ?? '#'}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex max-w-[12rem] items-center gap-1 rounded-md border bg-muted/40 px-2 py-1 text-xs hover:bg-muted"
          title={attachment.original_file_name}
        >
          <FileText className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="truncate">{attachment.original_file_name}</span>
          {onRemoveExisting && (
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault();
                onRemoveExisting(attachment.uuid);
              }}
              className="ml-1 text-muted-foreground hover:text-destructive"
              aria-label={`Remove ${attachment.original_file_name}`}
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </a>
      ))}

      {pendingFiles.map((file, index) => (
        <span
          key={`${file.name}-${index}`}
          className="inline-flex max-w-[12rem] items-center gap-1 rounded-md border border-dashed bg-muted/20 px-2 py-1 text-xs"
          title={file.name}
        >
          <FileText className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="truncate">{file.name}</span>
          {onRemovePending && (
            <button
              type="button"
              onClick={() => onRemovePending(index)}
              className="ml-1 text-muted-foreground hover:text-destructive"
              aria-label={`Remove ${file.name}`}
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </span>
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-8 px-2 text-xs"
        onClick={handleClick}
        disabled={isUploading}
      >
        {isUploading ? (
          <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
        ) : (
          <Paperclip className="mr-1 h-3.5 w-3.5" />
        )}
        {hasItems ? 'Add' : 'Attach File(s)'}
      </Button>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleChange}
      />
    </div>
  );
};
